import { inject } from '@angular/core';
import { Params, Route, Router, UrlSegment, UrlTree } from '@angular/router';

/**
 * The Policies list (`/governance/policies`) is the only Policies page. Every
 * older policy URL lands on it, so a bookmark or an old link still opens
 * something:
 *
 *   /governance/policies/location/:id   -> /governance/policies?place=:id
 *   /governance/policies/<anything>     -> /governance/policies   (keeps ?place= and ?scopeId=)
 *   /governance/proposals(/<anything>)  -> /governance/policies   (keeps ?place= and ?scopeId=)
 *
 * The list itself (`governance/policies`, exact) is declared before these.
 */
export const POLICY_LIST_PATH = '/governance/policies';

/** The query parameters the list understands, carried across a redirect. */
const CARRIED = ['place', 'scopeId'] as const;

/** Where an old policy URL goes. Pure, so a spec can call it without a router. */
export function legacyPolicyTarget(
  segments: readonly string[],
  query: Params,
): { readonly path: string; readonly queryParams: Params } {
  const queryParams: Params = {};
  for (const key of CARRIED) {
    if (typeof query[key] === 'string' && query[key]) queryParams[key] = query[key];
  }
  const isLocation = segments[0] === 'governance' && segments[1] === 'policies' && segments[2] === 'location';
  if (isLocation && segments[3]) queryParams['place'] = segments[3];
  return { path: POLICY_LIST_PATH, queryParams };
}

function redirect(segments: readonly UrlSegment[], query: Params): UrlTree {
  const target = legacyPolicyTarget(
    segments.map((s) => s.path),
    query,
  );
  return inject(Router).createUrlTree([target.path], { queryParams: target.queryParams });
}

/** Matches `governance/policies/<one or more more segments>`, never the list itself. */
function underPolicies(segments: UrlSegment[]) {
  return segments[0]?.path === 'governance' && segments[1]?.path === 'policies' && segments.length > 2
    ? { consumed: segments }
    : null;
}

/** Matches `governance/proposals` and anything under it. */
function proposals(segments: UrlSegment[]) {
  return segments[0]?.path === 'governance' && segments[1]?.path === 'proposals' ? { consumed: segments } : null;
}

export const LEGACY_POLICY_REDIRECTS: Route[] = [
  {
    matcher: underPolicies,
    redirectTo: ({ url, queryParams }) => redirect(url, queryParams),
  },
  {
    matcher: proposals,
    redirectTo: ({ url, queryParams }) => redirect(url, queryParams),
  },
];
